import React, { useState } from "react";
import dayjs from "dayjs";
import { ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { orangeTheme } from "../../components/theme";
import { getTimeLeft } from "./useShareDetails";

const PRESETS = [
    { value: "6", unit: "hours", h: 6 },
    { value: "12", unit: "hours", h: 12 },
    { value: "1", unit: "day", h: 24 },
    { value: "3", unit: "days", h: 72 },
    { value: "1", unit: "week", h: 168 },
];

const getCountdown = (expiryAt) => {
    const mins = expiryAt.diff(dayjs(), 'minute');
    const days = Math.floor(mins / 1440);
    const hrs = Math.floor((mins % 1440) / 60);
    const rem = mins % 60;
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}${hrs > 0 ? ` ${hrs} hr` : ''} from now`;
    if (hrs > 0) return `${hrs} hr${rem > 0 ? ` ${rem} min` : ''} from now`;
    return `${rem} min from now`;
};

// inline "extend link" panel for the edit page - new expiry is always counted from now,
// and the backend gets whole hours (same rounding as generating a share link in edit case)
const ExtendExpiryPanel = ({ shareData, now, onSubmit, isUpdating }) => {
    const [selectedPreset, setSelectedPreset] = useState(6); // hours of the picked tile, or "custom"
    const [customExpiryAt, setCustomExpiryAt] = useState(dayjs().add(1, 'hour'));

    const isExpired = !!shareData?.isExpired;
    const timeLeft = getTimeLeft(shareData, now);

    // presets are re-derived from the current time on every render so the preview never goes stale
    const expiryAt = selectedPreset === "custom" ? customExpiryAt : dayjs().add(selectedPreset, 'hour');
    const isExpiryValid = expiryAt && expiryAt.isValid() && expiryAt.diff(dayjs(), 'minute') >= 1;

    const handleSubmit = () => {
        if (!isExpiryValid) return;
        // Backend accepts whole hours — round up so the link never expires before the selected time.
        const hours = Math.max(1, Math.ceil(expiryAt.diff(dayjs(), 'minute') / 60));
        onSubmit(hours);
    };

    return (
        <div className={`ep ${isExpired ? "ep-expired" : ""}`}>
            <div className="ep-head">
                <span className="ep-head-icon">
                    <span className="material-symbols-outlined">{isExpired ? "link_off" : "update"}</span>
                </span>
                <div className="flex-grow-1">
                    <div className="ep-title">{isExpired ? "Reactivate this link" : "Extend this link"}</div>
                    <div className="ep-subtitle">
                        {isExpired
                            ? "This link has stopped working. Pick how long it should work again — the same link starts working right away."
                            : "Give people more time to open this link. The new time is counted from now."}
                    </div>
                </div>
                <span className={`ep-current ep-current-${timeLeft.tone}`}>
                    <i className={`mdi ${isExpired ? "mdi-timer-off-outline" : "mdi-timer-sand"}`}></i>
                    {timeLeft.label}
                </span>
            </div>

            <div className="ep-body">
                <div className="ep-step">1. Choose how long the link should stay active</div>
                <div className="ep-tiles">
                    {PRESETS.map((preset) => (
                        <button
                            key={preset.h}
                            type="button"
                            className={`ep-tile ${selectedPreset === preset.h ? "ep-tile-active" : ""}`}
                            onClick={() => setSelectedPreset(preset.h)}
                        >
                            <span className="ep-tile-value">{preset.value}</span>
                            <span className="ep-tile-unit">{preset.unit}</span>
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`ep-tile ${selectedPreset === "custom" ? "ep-tile-active" : ""}`}
                        onClick={() => {
                            setSelectedPreset("custom");
                            if (!customExpiryAt || customExpiryAt.diff(dayjs(), 'minute') < 1) setCustomExpiryAt(dayjs().add(1, 'hour'));
                        }}
                    >
                        <span className="material-symbols-outlined ep-tile-icon">calendar_month</span>
                        <span className="ep-tile-unit">Custom</span>
                    </button>
                </div>

                {selectedPreset === "custom" && (
                    <div className="ep-custom">
                        <label className="ep-custom-label">Pick the exact date &amp; time</label>
                        <ThemeProvider theme={orangeTheme}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    value={customExpiryAt}
                                    onChange={(newValue) => setCustomExpiryAt(newValue)}
                                    disablePast
                                    format="DD/MM/YYYY hh:mm A"
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            placeholder: "Select date and time",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </ThemeProvider>
                    </div>
                )}

                <div className="ep-step mt-4">2. Check the new expiry and update</div>
                <div className="ep-footer">
                    <div className={`ep-preview ${isExpiryValid ? "" : "ep-preview-invalid"}`}>
                        <span className="material-symbols-outlined">event_available</span>
                        {isExpiryValid ? (
                            <div>
                                <div className="ep-preview-label">New expiry</div>
                                <div className="ep-preview-date">{expiryAt.format("ddd, DD MMM YYYY · hh:mm A")}</div>
                                <div className="ep-preview-from">{getCountdown(expiryAt)}</div>
                            </div>
                        ) : (
                            <div>
                                <div className="ep-preview-label">New expiry</div>
                                <div className="ep-preview-date">Pick a future date and time</div>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className="ep-submit"
                        disabled={!isExpiryValid || isUpdating}
                        onClick={handleSubmit}
                    >
                        {isUpdating ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Updating...
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined">{isExpired ? "restart_alt" : "update"}</span>
                                {isExpired ? "Reactivate Link" : "Extend Link"}
                            </>
                        )}
                    </button>
                </div>
            </div>

            <style>
                {`
                    .ep {
                        background: #fff;
                        border: 1.5px solid #F2A341;
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 4px 16px rgba(242, 163, 65, 0.15);
                    }
                    .ep-head {
                        display: flex;
                        align-items: center;
                        gap: 14px;
                        padding: 16px 20px;
                        background: linear-gradient(135deg, #fff8ee 0%, #fff3e0 100%);
                        border-bottom: 1px solid #fde4c3;
                    }
                    .ep-expired .ep-head {
                        background: linear-gradient(135deg, #fff5f5 0%, #fff3e0 100%);
                    }
                    .ep-head-icon {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 44px;
                        height: 44px;
                        border-radius: 12px;
                        background: #F2A341;
                        color: #fff;
                        flex-shrink: 0;
                    }
                    .ep-head-icon .material-symbols-outlined { font-size: 24px; }
                    .ep-title { font-size: 17px; font-weight: 700; color: #1E222B; }
                    .ep-subtitle { font-size: 13px; color: #6b7280; margin-top: 2px; }
                    .ep-current {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                        padding: 6px 12px;
                        border-radius: 999px;
                        font-size: 12px;
                        font-weight: 600;
                        white-space: nowrap;
                        background: #fff;
                        border: 1px solid;
                    }
                    .ep-current .mdi { font-size: 15px; line-height: 1; }
                    .ep-current-ok { color: #046c4e; border-color: #a7f3d0; }
                    .ep-current-soon { color: #b45309; border-color: #F2A341; }
                    .ep-current-urgent { color: #c81e1e; border-color: #fca5a5; }
                    .ep-current-expired, .ep-current-none { color: #c81e1e; border-color: #f8b4b4; }

                    .ep-body { padding: 20px; }
                    .ep-step {
                        font-size: 12px;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: #6b7280;
                        margin-bottom: 10px;
                    }
                    .ep-tiles {
                        display: grid;
                        grid-template-columns: repeat(6, minmax(0, 1fr));
                        gap: 10px;
                    }
                    .ep-tile {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        gap: 2px;
                        min-height: 72px;
                        padding: 10px 6px;
                        border-radius: 10px;
                        border: 1.5px solid #e5e7eb;
                        background: #fff;
                        color: #1E222B;
                        cursor: pointer;
                        transition: border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
                    }
                    .ep-tile:hover { border-color: #F2A341; transform: translateY(-1px); }
                    .ep-tile-active {
                        border-color: #F2A341;
                        background: #fff3e0;
                        box-shadow: 0 0 0 3px rgba(242, 163, 65, 0.2);
                    }
                    .ep-tile-value { font-size: 22px; font-weight: 700; line-height: 1.1; }
                    .ep-tile-unit { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.4px; }
                    .ep-tile-icon { font-size: 24px; color: #1E222B; }
                    .ep-tile-active .ep-tile-value, .ep-tile-active .ep-tile-icon { color: #d97706; }
                    .ep-tile-active .ep-tile-unit { color: #b45309; }

                    .ep-custom { margin-top: 14px; max-width: 360px; }
                    .ep-custom-label { display: block; font-size: 13px; font-weight: 600; color: #444; margin-bottom: 6px; }

                    .ep-footer {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        flex-wrap: wrap;
                    }
                    .ep-preview {
                        flex: 1 1 280px;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        padding: 12px 16px;
                        border-radius: 10px;
                        background: #f8f9fa;
                        border: 1px dashed #cbd5e1;
                    }
                    .ep-preview > .material-symbols-outlined { font-size: 28px; color: #F2A341; }
                    .ep-preview-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; }
                    .ep-preview-date { font-size: 16px; font-weight: 700; color: #1E222B; }
                    .ep-preview-from { font-size: 12px; font-weight: 600; color: #b45309; }
                    .ep-preview-invalid .ep-preview-date { color: #9ca3af; font-weight: 600; font-size: 14px; }

                    .ep-submit {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        min-width: 200px;
                        padding: 14px 24px;
                        border: none;
                        border-radius: 10px;
                        background: #F2A341;
                        color: #fff;
                        font-size: 15px;
                        font-weight: 700;
                        cursor: pointer;
                        box-shadow: 0 4px 12px rgba(242, 163, 65, 0.35);
                        transition: filter 0.2s ease, transform 0.2s ease;
                    }
                    .ep-submit:hover:not(:disabled) { filter: brightness(1.05); transform: translateY(-1px); }
                    .ep-submit:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
                    .ep-submit .material-symbols-outlined { font-size: 20px; }

                    @media (max-width: 767px) {
                        .ep-head { flex-wrap: wrap; }
                        .ep-tiles { grid-template-columns: repeat(3, minmax(0, 1fr)); }
                        .ep-submit { width: 100%; }
                    }
                `}
            </style>
        </div>
    );
};

export default ExtendExpiryPanel;
